import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import { captureMessage } from '@sentry/react'
import { groupBy, orderBy } from 'lodash-es'

import { type RequestCardData } from '@/components/molecules/Card'
import { type CardAllocatedData } from '@/components/molecules/CardAllocated'
import { type CardAssignedData } from '@/components/molecules/CardAssigned'
import { type CardCompletedData } from '@/components/molecules/CardCompleted'
import { type CardOnboardedData } from '@/components/molecules/CardOnboarded'
import { type CardPendingData } from '@/components/molecules/CardPending'
import { type CompletedTransportRequestStatus, type TeamCarData } from '@/components/molecules/Status'
import { type NavigationLink } from '@/components/organisms/Navigation'
import { type AppViewProps } from '@/components/templates/AppView'
import { type Team } from '@/model/contexts/TeamsProvider.tsx'
import { useRegion } from '@/model/contexts/useRegion.ts'
import { type TransportRequest } from '@/model/contexts/useTransportRequests.ts'
import { teamCarType } from '@/pages/dashboard/data/teamCar'
import { transportRequestType } from '@/pages/dashboard/data/transportRequestType'

type RequestType = NavigationLink['value']

const pendingEvent = (request: TransportRequest): string | undefined => {
  switch (request.dashboardPendingRequest?.type) {
    case 'piloteAssignTransportRequest':
      return 'assignRequest'
    case 'piloteCancelTransportRequest':
      return 'cancelRequest'
    case 'piloteAllocateTransportRequest':
      return 'allocateRequest'
    case 'piloteOnboardTransportRequest':
      return 'customerTakenCareOf'
    case 'piloteReactivateTransportRequest':
      return 'reactivateRequest'
    case 'pilotePutOnHoldTransportRequest':
      return 'putOnHoldRequest'
    case 'piloteCustomerNotFound':
      return 'customerNotFound'
    case 'piloteCompleteTransportRequest':
      return 'arrivedAtDestination'
    case 'piloteUpdateTransportRequestDonation':
      return 'changeDonation'
    default:
      return request.dashboardPendingRequest?.type
  }
}

const completedStatus = (request: TransportRequest): CompletedTransportRequestStatus => {
  switch (request.status) {
    case 'unfindable':
      return 'unfindabled'
    case 'cancelled':
      return 'cancelled'
    default:
      return 'completed'
  }
}

const donationState = (
  request: TransportRequest
): 'cash' | 'digital' | 'textSent' | 'textClicked' | 'no' | undefined => {
  if (request.donation?.value && request.donation?.status !== 'COMPLETE') {
    return 'cash'
  }

  switch (request.donation?.status) {
    case 'NOT_STARTED':
      return 'no'
    case 'SMS_SENT':
      return 'textSent'
    case 'SMS_LINK_CLICKED':
      return 'textClicked'
    case 'COMPLETE':
      return 'digital'
    default:
      return undefined
  }
}

const pendingTeamCar = (request: TransportRequest, teams: ReadonlyArray<Team>): TeamCarData => {
  if (request.dashboardPendingRequest?.type === 'piloteAssignTransportRequest') {
    const teamIdToBeAssigned = request.dashboardPendingRequest?.payload?.teamId
    const team = teams.find(({ _id }) => _id === teamIdToBeAssigned)
    if (team) {
      return {
        carNumber: team.number,
        carType: team.vehicle && teamCarType(team.vehicle),
      }
    }
  }
  const team = teams.find(({ _id }) => _id === request.plannedTeamAssignment?.teamId)
  if (team) {
    return {
      carNumber: team.number,
      carType: team.vehicle && teamCarType(team.vehicle),
    }
  }

  return {
    carNumber: '',
    carType: request.customerVehicle.manualTransmission ? 'manual' : 'automatic',
  }
}

const teamCar = (request: TransportRequest) => {
  if (!request.team) {
    captureMessage('Transport Request has no team', {
      level: 'error',
      extra: { transportRequest: request },
    })
    return {
      carNumber: '?',
      usingMobileApp: true,
    }
  }
  return {
    carNumber: request.team.number,
    carType: teamCarType(request.team.vehicle),
    usingMobileApp: request.team.usingMobileApplication,
    vip: request.team.vip,
  }
}

type TransportRequestCardList = AppViewProps['data']['requestList']

const automatedStatus = (request: TransportRequest) => {
  switch (true) {
    case !request.availableForAutomation:
      return 'inactive'
    case !!request.plannedTeamAssignment?.teamId:
      return 'active'
    default:
      return 'pending'
  }
}

export const useTransportRequestCardList = ({
  transportRequests,
  teams,
  requestSortBy,
  completedRequestSortBy,
  baseUrl,
}: {
  transportRequests: ReadonlyArray<TransportRequest & { hidden: boolean }>
  teams: ReadonlyArray<Team>
  requestSortBy: 'asc' | 'desc'
  completedRequestSortBy: 'asc' | 'desc'
  baseUrl: string
}) => {
  const {
    region: { isAutomatedAssignmentEnabled },
  } = useRegion()
  const intl = useIntl()
  return useMemo<TransportRequestCardList>(() => {
    const requests = groupBy(
      orderBy(transportRequests, 'submitTimestamp', requestSortBy),
      transportRequestType
    ) as Record<RequestType, Array<TransportRequest & { hidden: boolean }>>
    const assigned = orderBy(requests.assigned, 'assignmentTimestamp', requestSortBy)
    const allocated = orderBy(requests.allocated, 'allocationTimestamp', requestSortBy)
    const onboarded = orderBy(requests.onboarded, 'onboardTimestamp', requestSortBy)
    const completed = orderBy(requests.completed, 'completionTimestamp', completedRequestSortBy)

    const buildRequest = (request: TransportRequest): RequestCardData => ({
      piloteId: String(request.piloteId),
      piloteIdUrl: `${baseUrl}/${request.piloteId}`,
      addressFrom: request.origin.formattedAddress,
      addressTo: request.destination.formattedAddress,
      date: intl.formatMessage({ id: 'requestList.submitTimestamp' }, { date: request.submitTimestamp }),
      detail: intl.formatMessage(
        { id: 'requestList.passengerInformation' },
        {
          numberOfPassengers: request.numberOfPassengers,
          numberOfSeats: request.customerVehicle.seats,
        }
      ),
      distanceTime: !request.evaluatedDistance
        ? '?'
        : intl.formatMessage(
            { id: 'requestList.evaluatedDistanceTime' },
            {
              distanceInMeters: request.evaluatedDistance.distanceInMeters,
              durationInSeconds: request.evaluatedDistance.durationInSeconds,
            }
          ),
      fullName: `${request.customerFirstName} ${request.customerLastName}`,
      phone: request.customerPhoneNumber,
      isMobileRequest: request.type === 'mobile',
      internalNote: request.internalNote ?? '',
      cancellationReason: request.cancellationReason
        ? intl.formatMessage({ id: `cancellationReason.${request.cancellationReason}` })
        : undefined,
    })

    return {
      pending:
        requests.pending?.map((request) => ({
          id: request._id,
          hidden: request.hidden,
          request: {
            ...buildRequest(request),
            automatedStatus: isAutomatedAssignmentEnabled ? automatedStatus(request) : undefined,
          },
          pendingEvent: pendingEvent(request) as CardPendingData['pendingEvent'],
          team: pendingTeamCar(request, teams),
          locationLabel: request.letters,
          status: {
            description: intl.formatMessage({ id: 'requestList.waitTime.pending' }),
            since: request.submitTimestamp,
            willExecuteAt: request.plannedTeamAssignment?.willExecuteAt,
          },
        })) ?? [],
      assigned:
        assigned?.map((request) => ({
          id: request._id,
          hidden: request.hidden,
          request: {
            ...buildRequest(request),
            ...(request.assignedBy === null ? { automatedStatus: 'active' } : {}),
          },
          pendingEvent: pendingEvent(request) as CardAssignedData['pendingEvent'],
          locationLabel: request.letters,
          team: teamCar(request),
          status: {
            description: intl.formatMessage({ id: 'requestList.waitTime.assigned' }),
            since: request.assignmentTimestamp,
          },
        })) ?? [],
      allocated:
        allocated?.map((request) => ({
          id: request._id,
          hidden: request.hidden,
          request: {
            ...buildRequest(request),
            ...(request.assignedBy === null ? { automatedStatus: 'active' } : {}),
          },
          pendingEvent: pendingEvent(request) as CardAllocatedData['pendingEvent'],
          locationLabel: request.letters,
          team: teamCar(request),
          status: {
            description: intl.formatMessage({ id: 'requestList.waitTime.allocated' }),
            since: request.allocationTimestamp,
          },
        })) ?? [],
      onboarded:
        onboarded?.map((request) => ({
          id: request._id,
          hidden: request.hidden,
          request: {
            ...buildRequest(request),
            ...(request.assignedBy === null ? { automatedStatus: 'active' } : {}),
          },
          pendingEvent: pendingEvent(request) as CardOnboardedData['pendingEvent'],
          locationLabel: request.letters,
          team: teamCar(request),
          status: {
            description: intl.formatMessage({ id: 'requestList.waitTime.onboarded' }),
            since: request.onboardTimestamp,
          },
        })) ?? [],
      completed:
        completed?.map((request) => ({
          id: request._id,
          hidden: request.hidden,
          request: {
            ...buildRequest(request),
            ...(request.assignedBy === null ? { automatedStatus: 'active' } : {}),
          },
          requestStatus: completedStatus(request),
          pendingEvent: pendingEvent(request) as CardCompletedData['pendingEvent'],
          locationLabel: request.letters,

          ...(request.team && { team: teamCar(request) }),
          donation: request.donation && {
            value: request.donation.value
              ? intl.formatMessage({ id: 'currency' }, { value: request.donation.value })
              : '',
            state: donationState(request),
          },
          status: {
            description: request.completionTimestamp
              ? intl.formatMessage({ id: 'requestList.waitTime.completed' }, { date: request.completionTimestamp })
              : '',
          },
        })) ?? [],
    }
  }, [intl, transportRequests, teams, requestSortBy, completedRequestSortBy, baseUrl, isAutomatedAssignmentEnabled])
}
